// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * Finding a filed machine (design doc §9.4, US-C-01).
 *
 * One box searches archive number, device number and serial, because the person holding
 * the machine does not know which of the three they have — they have a label. Archive and
 * device numbers match anywhere in the value; a serial matches whole, because it is
 * scanned rather than typed.
 *
 * Export is an ordinary link: the response is a CSV download carrying the same filters,
 * and routing it through `fetch` would mean holding the whole file in memory to hand it
 * back to the browser anyway.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { api, exportUrls } from "@/api/endpoints";
import type { ArchiveSearchHit, PartFate } from "@/api/types";
import { moduleTypeLabel } from "@/components/BomModulesSummary";
import { ErrorBanner } from "@/components/ErrorBanner";
import { IconButton } from "@/components/IconButton";
import { ScrapIcon } from "@/components/icons";
import { Pager } from "@/components/Pager";
import { ResizableTable } from "@/components/ResizableTable";
import { type ModuleFate, ScrapDialog } from "@/components/ScrapDialog";
import { StatusBadge } from "@/components/StatusBadge";
import { useCursorList } from "@/hooks/useCursorList";
import { usePermissions } from "@/hooks/usePermissions";
import { ModuleChain } from "./ArchiveDetailPage";

export function ArchiveSearch() {
  const { canRecord } = usePermissions();
  const { t, i18n } = useTranslation();
  // The filters live in the query string, which is what makes a filtered list something
  // you can link to: the product-type list points here with `?product_type_id=…`, and a
  // reader who found the right list can send it to someone else, or reload without losing
  // it. The component keeps a draft so typing does not rewrite history on every keystroke.
  const [params, setParams] = useSearchParams();
  const filters = useMemo(
    () => ({
      q: params.get("q") ?? "",
      product_type_id: params.get("product_type_id") ?? "",
      status: params.get("status") ?? "",
      // Set only by a link — the BOM list's archive count — so it has no box of its own,
      // just a chip saying it is in effect and a way to drop it.
      source_bom_id: params.get("source_bom_id") ?? "",
      // Robots, modules, or one module type (§9.8), and for modules whether fitted.
      kind: params.get("kind") ?? "",
      module_type: params.get("module_type") ?? "",
      module_state: params.get("module_state") ?? "",
    }),
    [params],
  );
  const [draft, setDraft] = useState(filters);
  const [scrapping, setScrapping] = useState<ArchiveSearchHit | null>(null);
  const queryClient = useQueryClient();

  // Scrapping is offered here rather than on the record: this is the page where someone is
  // looking at a fleet and deciding which machines are still in service (§8.7).
  const scrap = useMutation({
    mutationFn: ({
      archive,
      reason,
      modules,
      parts,
    }: {
      archive: ArchiveSearchHit;
      reason: string;
      modules: Record<string, ModuleFate>;
      parts: Record<string, PartFate>;
    }) => api.archives.scrap(archive.id, archive.current_version, reason, modules, parts),
    onSuccess: async () => {
      setScrapping(null);
      await queryClient.invalidateQueries({ queryKey: ["archives"] });
    },
  });

  // A link that arrives with different parameters — from the product-type list, or from
  // the back button — has to be reflected in the boxes as well as in the results.
  useEffect(() => setDraft(filters), [filters]);

  const applyFilters = (next: typeof filters) => {
    const cleaned = Object.entries(next).filter(([, value]) => value !== "");
    setParams(Object.fromEntries(cleaned), { replace: true });
  };

  const productTypes = useCursorList(["product-types", "all"], (cursor) =>
    api.productTypes.list({ cursor, limit: 200 }),
  );

  const archives = useCursorList(["archives", filters], (cursor) =>
    api.archives.search({
      ...filters,
      kind: (filters.kind || undefined) as "ROBOT" | "MODULE" | undefined,
      module_state: (filters.module_state || undefined) as "IN_STOCK" | "INSTALLED" | undefined,
      cursor,
      limit: 50,
    }),
  );
  const moduleTypes = useQuery({ queryKey: ["module-types"], queryFn: api.moduleTypes.list });
  const typeLabel = (code: string) =>
    moduleTypeLabel(code, moduleTypes.data ?? [], i18n.language === "en-US");
  /** The one 「类型」 select: "", ROBOT, MODULE, or MODULE:<letter>. */
  const kindValue = draft.module_type ? `MODULE:${draft.module_type}` : draft.kind;

  // The version number is what the reader recognises; the id in the URL is not.
  const filterBom = useQuery({
    queryKey: ["bom", filters.source_bom_id],
    queryFn: () => api.boms.get(filters.source_bom_id),
    enabled: Boolean(filters.source_bom_id),
  });

  const showSn = archives.items.some((archive) => archive.robot_sn || archive.module_sn);
  const showFoundAt = archives.items.some((archive) => archive.matched_position_name);
  const showChain = archives.items.some((archive) => archive.kind === "MODULE");

  return (
    <section>
      <h1>{t("archive.searchTitle")}</h1>

      <form
        className="filters"
        onSubmit={(event) => {
          event.preventDefault();
          applyFilters(draft);
        }}
      >
        <input
          aria-label={t("archive.searchPlaceholder")}
          placeholder={t("archive.searchPlaceholder")}
          value={draft.q}
          onChange={(event) => setDraft({ ...draft, q: event.target.value })}
        />
        <select
          aria-label={t("archive.productType")}
          value={draft.product_type_id}
          onChange={(event) => applyFilters({ ...draft, product_type_id: event.target.value })}
        >
          <option value="">{t("archive.productType")}</option>
          {productTypes.items.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
        <select
          aria-label={t("archive.status")}
          value={draft.status}
          onChange={(event) => applyFilters({ ...draft, status: event.target.value })}
        >
          <option value="">{t("archive.status")}</option>
          <option value="ACTIVE">{t("archive.statusActive")}</option>
          <option value="SCRAPPED">{t("archive.statusScrapped")}</option>
        </select>
        <select
          aria-label={t("archive.kind")}
          value={kindValue}
          onChange={(event) => {
            const [kind, moduleType = ""] = event.target.value.split(":");
            applyFilters({
              ...draft,
              kind: kind ?? "",
              module_type: moduleType,
              // Fitted or in stock is a question about modules only.
              module_state: kind === "MODULE" ? draft.module_state : "",
            });
          }}
        >
          <option value="">{t("archive.kind")}</option>
          <option value="ROBOT">{t("archive.kindRobot")}</option>
          <option value="MODULE">{t("archive.kindModule")}</option>
          {(moduleTypes.data ?? [])
            .filter((type) => type.status === "ACTIVE")
            .map((type) => (
              <option key={type.code} value={`MODULE:${type.code}`}>
                {"　"}
                {typeLabel(type.code)}
              </option>
            ))}
        </select>
        {draft.kind === "MODULE" && (
          <select
            aria-label={t("archive.moduleState")}
            value={draft.module_state}
            onChange={(event) => applyFilters({ ...draft, module_state: event.target.value })}
          >
            <option value="">{t("archive.moduleState")}</option>
            <option value="IN_STOCK">{t("moduleArchive.inStock")}</option>
            <option value="INSTALLED">{t("archive.moduleInstalled")}</option>
          </select>
        )}
        <button type="submit">{t("common.search")}</button>
        <a className="link-button" href={exportUrls.archives(filters)}>
          {t("common.export")}
        </a>
      </form>

      {/* Every filter the list below is narrowed by, in one row above it: the reader sees
          the whole condition in one place and drops any part with its ×. The BOM filter
          arrives by link and has no box of its own, so this is the only place it shows;
          the search term's box can be edited without being applied, so the chip is what
          says what actually is. */}
      {(filters.q ||
        filters.product_type_id ||
        filters.status ||
        filters.source_bom_id ||
        filters.kind) && (
        <div className="filter-chips">
          {filters.q && (
            <FilterChip
              label={t("archive.searchFilter", { q: filters.q })}
              onClear={() => applyFilters({ ...filters, q: "" })}
            />
          )}
          {filters.product_type_id && (
            <FilterChip
              label={t("archive.productTypeFilter", {
                name:
                  productTypes.items.find((type) => type.id === filters.product_type_id)?.name ??
                  "…",
              })}
              onClear={() => applyFilters({ ...filters, product_type_id: "" })}
            />
          )}
          {filters.status && (
            <FilterChip
              label={t("archive.statusFilter", {
                status: t(
                  filters.status === "ACTIVE"
                    ? "archive.statusActive"
                    : filters.status === "SCRAPPED"
                      ? "archive.statusScrapped"
                      : filters.status,
                ),
              })}
              onClear={() => applyFilters({ ...filters, status: "" })}
            />
          )}
          {filters.source_bom_id && (
            <FilterChip
              label={t("archive.bomFilter", {
                version: filterBom.data
                  ? `${filterBom.data.version}${filterBom.data.name ? ` · ${filterBom.data.name}` : ""}`
                  : "…",
              })}
              onClear={() => applyFilters({ ...filters, source_bom_id: "" })}
            />
          )}
          {filters.kind && (
            <FilterChip
              label={t("archive.kindFilter", {
                kind: filters.module_type
                  ? typeLabel(filters.module_type)
                  : t(filters.kind === "MODULE" ? "archive.kindModule" : "archive.kindRobot"),
              })}
              onClear={() =>
                applyFilters({ ...filters, kind: "", module_type: "", module_state: "" })
              }
            />
          )}
          {filters.module_state && (
            <FilterChip
              label={
                filters.module_state === "IN_STOCK"
                  ? t("moduleArchive.inStock")
                  : t("archive.moduleInstalled")
              }
              onClear={() => applyFilters({ ...filters, module_state: "" })}
            />
          )}
        </div>
      )}
      <ErrorBanner error={archives.error} />

      {/* The SN and found-at columns appear only when some row has something to put in
          them: archives filed before SNs existed have none, and only a serial search names
          a position. Each column set keeps its own widths. */}
      <ResizableTable
        storageKey={`archive-search${showSn ? "-sn" : ""}${showFoundAt ? "-found" : ""}${showChain ? "-chain" : ""}`}
      >
        <thead>
          <tr>
            <th>{t("archive.archiveNo")}</th>
            <th>{t("archive.device")}</th>
            {showSn && <th>{t("robotSn.label")}</th>}
            {showFoundAt && <th>{t("archive.foundAt")}</th>}
            {showChain && <th>{t("moduleArchive.location")}</th>}
            <th>{t("archive.status")}</th>
            <th>{t("archive.version")}</th>
            <th>{t("archive.createdAt")}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {archives.items.map((archive) => (
            <tr key={archive.id}>
              {/* The module type follows the archive number, as everywhere an archive is named. */}
              <td className="archive-no">
                <Link to={`/archives/${archive.id}`}>{archive.archive_no}</Link>
                {archive.kind === "MODULE" && (
                  <>
                    {" "}
                    <span className="module-badge">{typeLabel(archive.module_type ?? "")}</span>
                  </>
                )}
              </td>
              {/* A module has no device number: the column stays empty, and its module SN
                  goes under SN. */}
              <td className="mono">{archive.device_no ?? <span className="muted">—</span>}</td>
              {showSn && (
                <td className="mono">
                  {(archive.kind === "MODULE" ? archive.module_sn : archive.robot_sn) ?? (
                    <span className="muted">—</span>
                  )}
                </td>
              )}
              {showFoundAt && (
                <td>
                  {/* Where the searched-for serial was last seen. Blank unless the row was
                    found by a serial — a search for a device number has no one position to
                    name. A past sighting is labelled, because "was on this machine" and "is
                    on this machine" are different answers and the row cannot show both. */}
                  {archive.matched_position_name ? (
                    <>
                      {archive.matched_position_name}{" "}
                      <span className="serial muted">{archive.matched_serial}</span>{" "}
                      {/* A past sighting says so in words, with the version it was last seen
                        in: a bare "v2" beside a machine's current location read as where
                        the part is now. On a scrapped machine the status says why it is
                        past, so the version alone. */}
                      {archive.matched_is_current === false && (
                        <span className="badge badge-past">
                          {archive.status === "SCRAPPED"
                            ? `v${archive.matched_version_no}`
                            : t("archive.foundBefore", { version: archive.matched_version_no })}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
              )}
              {showChain && (
                <td>
                  {archive.kind === "MODULE" && archive.status === "ACTIVE" ? (
                    <ModuleChain chain={archive.installed_on ?? []} typeLabel={typeLabel} />
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
              )}
              <td>
                <StatusBadge status={archive.status} />
              </td>
              <td>
                <span className="badge badge-version">v{archive.current_version}</span>
              </td>
              <td>{new Date(archive.created_at).toLocaleString()}</td>
              <td className="row-actions">
                <IconButton
                  label={t("archive.scrap")}
                  icon={<ScrapIcon />}
                  danger
                  // A fitted module goes with its robot, or comes out by a change first (§9.7).
                  disabled={
                    !canRecord ||
                    archive.status !== "ACTIVE" ||
                    (archive.installed_on ?? []).length > 0
                  }
                  disabledReason={
                    !canRecord
                      ? t("roles.readOnly")
                      : archive.status !== "ACTIVE"
                        ? t("archive.scrappedReason")
                        : t("archive.installedModuleReason")
                  }
                  onClick={() => setScrapping(archive)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </ResizableTable>

      {archives.items.length === 0 && !archives.isLoading && (
        <p className="muted">{t("common.empty")}</p>
      )}
      {scrapping && (
        <ScrapDialog
          archive={scrapping}
          pending={scrap.isPending}
          error={<ErrorBanner error={scrap.error} />}
          onScrap={(reason, modules, parts) =>
            scrap.mutate({ archive: scrapping, reason, modules, parts })
          }
          onClose={() => {
            scrap.reset();
            setScrapping(null);
          }}
        />
      )}

      {scrap.data && (
        <div className="banner banner-info">
          {t("archive.scrapReleased", { count: scrap.data.released_serials.length })}
          {(scrap.data.parts ?? []).length > 0 &&
            ` ${t("scrap.partsRecorded", {
              scrapped: (scrap.data.parts ?? []).filter((p) => p.outcome === "SCRAPPED").length,
              restocked: (scrap.data.parts ?? []).filter((p) => p.outcome === "RESTOCKED").length,
            })}`}
        </div>
      )}

      <Pager
        hasNextPage={archives.hasNextPage}
        isFetchingNextPage={archives.isFetchingNextPage}
        onMore={() => void archives.fetchNextPage()}
      />
    </section>
  );
}

function FilterChip({ label, onClear }: { label: string; onClear: () => void }) {
  const { t } = useTranslation();
  return (
    <span className="filter-chip">
      {label}
      {/* An × says "remove" without a word; the accessible name says what it removes. */}
      <button
        type="button"
        className="filter-chip-clear"
        aria-label={t("archive.clearFilter", { label })}
        onClick={onClear}
      >
        ×
      </button>
    </span>
  );
}
